import React, { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Checkbox, Chip, LinearProgress, Tooltip } from "@material-ui/core";
import * as _ from "lodash";
import { showToast } from "store/slices/toast";

import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";
import { Link } from "react-router-dom";

import AppUtil from "utilities/AppUtil";
import AppColor from "utilities/AppColor";
import InputSearch from "components/Input/InputSearch";
import AppTable from "../../Table/AppTable";
import TablePaginationActions from "../../Table/TablePaginationActions";
import {
    fetchClientListPaging,
    getClientList,
    getClientListPaging,
    setClientListPagingQueries,
    resendIntakeLink,
} from "store/slices/client";
import { getAuthInfo } from "store/slices/auth";
import PropTypes from "prop-types";
import AppConfirmDialog from "components/Dialog/AppConfirmDialog";

const headCells = [
  { id: "full_name", numeric: "left", disablePadding: false, label: "label.participant_name", sort: true },
  { id: "ndisPlanNumber", numeric: "left", disablePadding: false, label: "label.ndisPlanNumber", sort: true },
  { id: "house", numeric: "left", disablePadding: false, label: "label.house", sort: false },
  { id: "silLevel", numeric: "left", disablePadding: false, label: "label.sil_level", sort: false },
  { id: "planType", numeric: "left", disablePadding: false, label: "label.plan_type", sort: false },
  { id: "approvalStatus", numeric: "left", disablePadding: false, label: "label.status", sort: true },
  { id: "action", numeric: "center", disablePadding: false, label: "label.actions", sort: false },
];

const TableInformation = forwardRef(({ selected, setSelected }, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const authInfo = useSelector(getAuthInfo);
  const savedListPaging = useSelector(getClientListPaging);

  const [queries, setQueries] = useState({
    asc_sort: savedListPaging.queries.asc_sort,
    page: savedListPaging.queries.page,
    size: savedListPaging.queries.size || 10,
    search_key: savedListPaging.queries.search_key,
    sort_field: savedListPaging.queries.sort_field,
  });
  const clientList = useSelector(getClientList);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalElements, setTotalElements] = useState(savedListPaging.totalElements || 0);
  const [searchKey, setSearchKey] = useState(savedListPaging.queries.search_key || "");
  const [showResendConfirmModal, setShowResendConfirmModal] = useState(false);
  const [selectedClientToSend, setSelectedClientToSend] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);

  const handleClickSendIntakeLink = (clientRow) => {
    setSelectedClientToSend(clientRow);
    setShowResendConfirmModal(true);
  };

  const handleResendIntakeLink = async () => {
    if (!selectedClientToSend) return;
    setResendLoading(true);
    try {
      const res = await dispatch(resendIntakeLink(selectedClientToSend.id));
      if (res.payload) {
        setShowResendConfirmModal(false);
        setSelectedClientToSend(null);
        _loadData();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setResendLoading(false);
    }
  };

  const emptyRows = data.length === 0;

  const _handleSelect = (id) => {
    if (!selected.includes(id)) {
      setSelected([...selected, id]);
    } else {
      setSelected(selected.filter((item) => item !== id));
    }
  };

  const _handleSelectAll = () => {
    if (data.length === countSelectInPage()) {
      setSelected([]);
    } else {
      setSelected(_.union(selected, data.map((item) => item.id)));
    }
  };

  const handleChangePage = (event, newPage) => {
    _loadData({ ...queries, page: newPage + 1 });
    AppUtil.scrollToTopById("main-panel");
  };

  const handleChangeRowsPerPage = (event) => {
    _loadData({ ...queries, page: 1, size: parseInt(event.target.value) });
    AppUtil.scrollToTopById("main-panel");
  };

  const onRequestSort = (event, property) => {
    _loadData({
      ...queries,
      sort_field: property,
      asc_sort: property === queries.sort_field ? queries.asc_sort !== true : true,
    });
  };

  const onSearch = (value) => {
    setSearchKey(value);
    _loadData({ ...queries, search_key: value, page: 1 });
  };

  const _loadData = async ({ page, size, search_key, asc_sort, sort_field } = {}) => {
    const newQueries = { ...queries };
    if (page) newQueries.page = page;
    if (size) newQueries.size = size;
    if (sort_field) newQueries.sort_field = sort_field;
    newQueries.search_key = search_key || "";
    newQueries.asc_sort = !(!asc_sort || false);
    newQueries.companyId = authInfo.companyId;
    setLoading(true);
    const result = await dispatch(fetchClientListPaging(newQueries));
    const { payload } = result;

    if (payload) {
      setQueries(newQueries);
      setTotalElements(payload.totalElements);
      setLoading(false);
      dispatch(setClientListPagingQueries({
        page: newQueries.page,
        size: newQueries.size,
        search_key: newQueries.search_key,
        sort_field: newQueries.sort_field,
        asc_sort: newQueries.asc_sort,
      }));
    } else if (newQueries.page > 1) {
      _loadData({ ...newQueries, page: newQueries.page - 1 });
    } else {
      setQueries(newQueries);
      setTotalElements(0);
      setData([]);
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    refreshData: () => _loadData(queries),
    loadWithQueries: (q) => _loadData(q),
    getCurrentQueries: () => queries,
    getDataLength: () => data.length,
  }));

  useEffect(() => {
    _loadData(queries);
  }, []);

  useEffect(() => {
    if (clientList) setData(clientList);
  }, [clientList]);

  const countSelectInPage = () =>
    data.filter((item) => selected.includes(item.id)).length;

  const formatPlanType = (billingDetail) => {
    switch (billingDetail) {
      case "NDIA": return t("label.agency");
      case "PLAN_MANAGER": return t("label.plan");
      case "SELF": return t("label.self");
      default: return billingDetail || "-";
    }
  };

  const getSilLevelStyle = (silLevel) => {
    switch (silLevel) {
      case "HIGH_INTENSITY":
        return { backgroundColor: "#EDE9FE", color: "#7C3AED", fontWeight: "600", fontSize: "12px" };
      case "STANDARD":
        return { backgroundColor: AppColor.mainLight, color: AppColor.green, fontWeight: "600", fontSize: "12px" };
      case "COMPLEX":
        return { backgroundColor: "#FEF3C7", color: "#D97706", fontWeight: "600", fontSize: "12px" };
      case "RESPITE":
        return { backgroundColor: "#E0F2FE", color: "#0369A1", fontWeight: "600", fontSize: "12px" };
      default:
        return {};
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "ACTIVE":
        return { backgroundColor: AppColor.mainLight, color: AppColor.green, fontWeight: "600", fontSize: "12px" };
      case "PENDING":
      case "WAITING_FOR_APPROVAL":
        return { backgroundColor: "#FFFBEB", color: AppColor.orange, fontWeight: "600", fontSize: "12px" };
      case "REJECT":
        return { backgroundColor: "#FFEAEB", color: AppColor.red, fontWeight: "600", fontSize: "12px" };
      default:
        return {};
    }
  };

  return (
    <div>
      <Box my={2}>
        <InputSearch
          onSearch={onSearch}
          searchText={searchKey}
          placeholder={t("label.search_participant_placeholder")}
          borderColor={AppColor.mainColor}
          iconColor="#374151"
          style={{ width: "100%" }}
        />
      </Box>
      <AppTable
        onRequestSort={onRequestSort}
        order={queries.asc_sort === true ? "asc" : "desc"}
        orderBy={queries.sort_field}
        rowCount={data.length}
        headCells={headCells}
        numSelected={countSelectInPage()}
        onSelectAllClick={_handleSelectAll}
      >
        <TableBody>
          {loading && (
            <TableRow>
              <TableCell colSpan={7} padding="none">
                <LinearProgress />
              </TableCell>
            </TableRow>
          )}
          {data.map((row, index) => (
            <TableRow key={index}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selected.indexOf(row.id) !== -1}
                  inputProps={{ "aria-labelledby": row.id }}
                  onClick={() => _handleSelect(row.id)}
                  color="primary"
                />
              </TableCell>
              <TableCell>
                <Link
                  to={`/admin/participants/${row.id}`}
                  style={{ color: "inherit", textDecoration: "none" }}
                  onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                  onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                >
                  {row.name}
                </Link>
              </TableCell>
              <TableCell>{row.ndisPlanNumber || "-"}</TableCell>
              <TableCell>-</TableCell>
              <TableCell>
                {row.silLevel ? (
                  <Chip
                    style={getSilLevelStyle(row.silLevel)}
                    size="small"
                    label={t(`sil_level.${row.silLevel}`)}
                  />
                ) : "-"}
              </TableCell>
              <TableCell>{formatPlanType(row.billingDetail)}</TableCell>
              <TableCell>
                <Chip
                  style={getStatusStyle(row.approvalStatus)}
                  size="small"
                  label={t(`approval_status.${row.approvalStatus}`)}
                />
              </TableCell>
              <TableCell align="center">
                <Link
                  to={`/admin/participants/${row.id}`}
                  style={{ display: "inline-block" }}
                >
                  <Button variant="text" style={{ minWidth: "auto", padding: 8 }}>
                    <i className="fas fa-edit" style={{ color: "#374151", fontSize: 16 }} />
                  </Button>
                </Link>
                <Tooltip title={t("label.send_intake_link")}>
                  <Button
                    variant="text"
                    style={{ minWidth: "auto", padding: 8 }}
                    onClick={() => handleClickSendIntakeLink(row)}
                  >
                    <i className="fas fa-paper-plane" style={{ color: "#374151", fontSize: 16 }} />
                  </Button>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {emptyRows && (
            <TableRow style={{ height: 53 }}>
              <TableCell colSpan={headCells.length} className="text-center">
                {t("label.no_data")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              colSpan={headCells.length}
              count={totalElements}
              rowsPerPage={queries.size}
              page={queries.page - 1}
              SelectProps={{
                inputProps: { "aria-label": "rows per page" },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              labelRowsPerPage={`${t("label.rows_per_page")}:`}
              labelDisplayedRows={({ from, to, count }) =>
                t("label.from_to", {
                  from,
                  to,
                  count: count !== -1 ? count : `${t("text.more_than")} ${to}`,
                })
              }
            />
          </TableRow>
        </TableFooter>
      </AppTable>
      
      <AppConfirmDialog
        open={showResendConfirmModal}
        onConfirm={handleResendIntakeLink}
        onClose={() => {
          setShowResendConfirmModal(false);
          setSelectedClientToSend(null);
        }}
        isDisabledButton={resendLoading}
        title="Resend Intake Link"
        content="Are you sure you want to generate a new token and resend the intake link email to this client?"
      />
    </div>
  );
});

TableInformation.displayName = "TableInformation";

TableInformation.propTypes = {
  selected: PropTypes.array.isRequired,
  setSelected: PropTypes.func.isRequired,
};

export default TableInformation;
